import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu, X,
  Globe, BookOpen, Users, LayoutGrid, Sparkles, GraduationCap, UsersRound, Mail,
  Clapperboard, Workflow, Award, Info, MessageCircle, Compass,
  LineChart, ClipboardList, TrendingUp, Trophy, Megaphone, Film,
  UserPlus, DollarSign, Handshake, Briefcase, Search, CreditCard, Edit3, LifeBuoy, ShieldOff, Sparkle, LogOut, Trash2, ExternalLink,
} from 'lucide-react';
import { useChannel } from '../contexts/ChannelContext';
import { useUserAuth } from '../contexts/UserAuthContext';
import { NavbarWordmark } from './ChannelWordmark';
import PulsePill from './PulsePill';
import NavbarAuthControl from './NavbarAuthControl';

/**
 * HamburgerOverlay — top-right hamburger opens a dark half-screen panel
 * that lists every section on the site with a short descriptive one-liner
 * under each link. Replaces the older left-side "League" dropdown pill.
 *
 * Layout: on ≥md screens the panel is ~half the viewport width so the
 * page peeks through. On mobile it takes the full width so text stays
 * readable one-handed.
 */
const HamburgerOverlay = ({ open, onClose, groups, user, onLogin }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while the overlay is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={`fixed inset-0 z-[90] transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
        data-testid="hamburger-backdrop"
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        className={`fixed top-0 right-0 z-[95] h-full w-full md:w-1/2 lg:w-[42%] xl:w-[38%] max-w-[560px] transform transition-transform duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          backgroundColor: 'rgba(10, 10, 12, 0.94)',
          backdropFilter: 'blur(32px) saturate(160%)',
          WebkitBackdropFilter: 'blur(32px) saturate(160%)',
          borderLeft: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '-24px 0 60px -16px rgba(0,0,0,0.65)',
        }}
        data-testid="hamburger-overlay"
      >
        {/* Specular edge to match nav */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-6 left-0 w-px"
          style={{ background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.28), transparent)' }}
        />

        <div className="flex items-center justify-between px-6 sm:px-8 h-16 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-[0.32em] text-gray-500">
            Menu
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full inline-flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/10 transition active:scale-90"
            aria-label="Close menu"
            data-testid="hamburger-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto h-[calc(100%-4rem)] px-4 sm:px-6 py-6 sm:py-8">
          {/* iter184 · Log-in CTA at the very top of the overlay for
              unauthenticated visitors. Blue pill matches the athlete
              "Business inquiries" pattern so it reads as a primary
              CTA. Fires the shared login modal via onLogin. */}
          {!user && (
            <button
              type="button"
              onClick={onLogin}
              data-testid="hamburger-login-cta"
              className="w-full mb-6 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-white font-semibold text-sm active:scale-[0.98] transition"
              style={{
                background: '#0A84FF',
                boxShadow: '0 8px 20px -8px rgba(10,132,255,0.55), inset 0 1px 0 rgba(255,255,255,0.15)',
                letterSpacing: '-0.01em',
              }}
            >
              Log in
            </button>
          )}
          {groups.map((group, gi) => (
            <div key={group.title || `g-${gi}`} className={gi > 0 ? 'mt-8' : ''}>
              {group.title && (
                <p className="px-3 mb-3 text-[11px] uppercase tracking-[0.28em] text-gray-500">
                  {group.title}
                </p>
              )}
              <ul className="space-y-1">
                {group.items.map((it) => {
                  const Icon = it.icon;
                  const isDestructive = it.destructive === true;
                  const textColor = isDestructive ? 'text-rose-400' : 'text-white';
                  const inner = (
                    <>
                      <span
                        className="flex-shrink-0 w-11 h-11 rounded-full inline-flex items-center justify-center ring-1 ring-white/10"
                        style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                      >
                        {/* iter668j · Hamburger icons now render pure
                            white across every persona / group per Jay
                            — the per-item `color` field is kept for
                            future accent variants but ignored here so
                            the drawer reads calm + unified instead of
                            candy-coloured. Destructive rows stay rose
                            via the `isDestructive` path below. */}
                        {Icon && (
                          <Icon
                            className="w-[18px] h-[18px]"
                            style={{ color: isDestructive ? '#f87171' : '#ffffff' }}
                            strokeWidth={1.75}
                          />
                        )}
                      </span>
                      <span className="min-w-0 flex-1 text-left">
                        <span className={`block text-[17px] sm:text-lg font-semibold ${textColor} leading-tight`}>
                          {it.label}
                        </span>
                        {it.subtitle && (
                          <span className="mt-0.5 block text-[13px] sm:text-sm text-gray-400 leading-snug">
                            {it.subtitle}
                          </span>
                        )}
                      </span>
                    </>
                  );
                  // iter516k · Items may now carry an `onClick` handler
                  // in place of a `to` route — used by Sign Out and
                  // Delete Account at the bottom of every persona menu.
                  const commonClasses = "group w-full flex items-center gap-4 px-3 py-3 rounded-2xl hover:bg-white/[0.04] transition-colors";
                  const testid = `hamburger-item-${it.label.toLowerCase().replace(/\s+/g, '-').replace(/[·]/g,'')}`;
                  return (
                    <li key={it.to || it.label}>
                      {it.onClick ? (
                        <button
                          type="button"
                          onClick={() => { it.onClick(); onClose(); }}
                          className={commonClasses}
                          data-testid={testid}
                        >
                          {inner}
                        </button>
                      ) : (
                        <Link
                          to={it.to}
                          onClick={onClose}
                          className={commonClasses}
                          data-testid={testid}
                        >
                          {inner}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          <p className="mt-10 pt-6 border-t border-white/10 text-[10px] uppercase tracking-[0.28em] text-gray-600 px-3">
            © Flyt League · Athlete-first storytelling
          </p>
        </div>
      </aside>
    </>,
    document.body
  );
};

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const channel = useChannel();
  const { user, setLoginOpen, logout } = useUserAuth() || {};
  const isFlyt = channel.id === 'flyt';
  const isVisionary = channel.id === 'dreams_visionary';
  const isNetworkRoute = !isFlyt;
  // iter816 · The new white-background Network Profile screen needs a
  // LIGHT navbar (black wordmark/tagline text) — everywhere else on the
  // site still defaults dark. `isNetworkProfilePage` also flips the
  // Pulse pill + hamburger button to a SOLID black treatment (matching
  // this screen's black-pill-on-white convention) instead of the
  // barely-visible tinted style the generic light navbar normally uses.
  const isNetworkProfilePage = location.pathname.startsWith('/network-profile');
  const isDark = !isNetworkProfilePage && (channel.theme === 'dark' || isNetworkRoute);
  const [menuOpen, setMenuOpen] = useState(false);
  // Persona of the signed-in user — used to render a dedicated console
  // group at the top of the hamburger overlay so agents/brands/programs
  // get their own workspace links alongside the generic site nav.
  const persona = (user?.persona || '').toLowerCase();

  // Close overlay whenever the route changes so back-nav feels clean.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Hamburger overlay content — every link + a descriptive subtitle so
  // first-time visitors immediately understand what each surface is.
  // Each item carries a lucide icon + a tinted stroke color so the panel
  // reads like the founder-console sidebar reference (circular icon left,
  // mixed-case title + muted subtitle right).
  let overlayGroups;
  if (isFlyt) {
    // iter516j · FLYT Studios hamburger group hidden globally while
    // athlete onboarding is the priority. Keep the item array in a
    // block-comment so we can restore it verbatim later. The Flyt
    // League group stays visible so the /flyt route isn't a dead
    // end for anyone who lands via direct URL / old bookmark.
    overlayGroups = [
      // {
      //   title: 'FLYT Studios',
      //   items: [
      //     { to: '/flyt', label: 'Studios', subtitle: 'The studio powering flyt league.', icon: Clapperboard, color: '#facc15' },
      //     { to: '/flyt/process', label: 'Process', subtitle: 'How we build brand + athlete stories.', icon: Workflow, color: '#22d3ee' },
      //     { to: '/flyt/brands', label: 'Brands', subtitle: "Case studies with the world's boldest brands.", icon: Award, color: '#f472b6' },
      //     { to: '/flyt/about', label: 'About', subtitle: 'Meet the studio team.', icon: Info, color: '#60a5fa' },
      //     { to: '/flyt/contact', label: 'Contact', subtitle: 'Start a project with FLYT Studios.', icon: MessageCircle, color: '#fb7185' },
      //   ],
      // },
      {
        title: 'Flyt League',
        items: [
          { to: '/', label: 'Network', subtitle: 'Where every athlete lives, breathes, moves.', icon: Globe, color: '#60a5fa' },
          { to: '/stories', label: 'Stories', subtitle: 'Long-form journals from the athletes shaping the league.', icon: BookOpen, color: '#38bdf8' },
        ],
      },
    ];
  } else if (isVisionary) {
    overlayGroups = [
      {
        items: [
          { to: '/visionary/network', label: 'Network', subtitle: "The visionary network — dreamers building what's next.", icon: Compass, color: '#a78bfa' },
          { to: '/visionary/stories', label: 'Dreamers Stories', subtitle: 'First-person journals from the dreamers.', icon: BookOpen, color: '#38bdf8' },
          { to: '/visionary/about', label: 'About', subtitle: 'What visionary stands for.', icon: Info, color: '#60a5fa' },
          { to: '/visionary/contact', label: 'Contact', subtitle: 'Reach the visionary team.', icon: Mail, color: '#fb7185' },
        ],
      },
    ];
  } else {
    // For athletes and fans we drop the "FLYT Studios" group — that's
    // Jay's B2B production arm and it clutters the athlete-facing
    // hamburger. Brands / programs / agents still see it because they
    // may want to hire the studio.
    // iter516j · FLYT Studios hamburger group hidden while athlete
    // onboarding is the primary focus. Keep the array + logic intact
    // — just short-circuit the visibility flag to false. Flip back
    // to the persona check when Studios is ready to promote again.
    const showFlytStudiosGroup = false;
    // const showFlytStudiosGroup = ['brand', 'program', 'agent', 'investor'].includes((persona || '').toLowerCase());
    // iter118 · Athletes shouldn't see Brands / Programs links —
    // there are no brand partners live yet, and the Programs page is
    // an ops surface for school admins, not athletes. Hide both from
    // the athlete hamburger so the menu only shows what's actually
    // usable to them. Everyone else (fans, brands, programs, agents,
    // and signed-out visitors) still sees the full nav.
    const hideBrandsAndPrograms = (persona || '').toLowerCase() === 'athlete';
    // iter221 · Navigation restructure — Brands + Programs now route to
    // the persona landing pages (/for-brands, /for-programs) instead of
    // the internal Studios pages, so new visitors get a "here's what
    // you'd get" pitch. Athletes + Agents get their own persona
    // landing links so anyone browsing knows exactly what the product
    // is for them.
    const leagueItems = [
      { to: '/', label: 'Network', subtitle: 'Where every athlete lives, breathes, moves.', icon: Globe, color: '#60a5fa' },
      { to: '/stories', label: 'Stories', subtitle: 'Long-form journals from the athletes shaping the league.', icon: BookOpen, color: '#38bdf8' },
      // iter688i · "Collections" removed everywhere per founder — not
      // ready to ship yet. Re-add here when curated drops go live.
      // Persona landing pages — visible to everyone so ads can point
      // straight at the right pitch.
      { to: '/for-athletes', label: 'For Athletes', subtitle: 'Your career, on one page. Forever.', icon: Trophy, color: '#fbbf24' },
      // iter816 · "For Agents" removed per Jay: there's no agent
      // dashboard built yet, so this pitch was sending people to sign
      // up for something that doesn't exist. Route + page kept intact
      // (infra preserved) so it's a one-line restore once that's built.
      // iter583 · "For Brands" removed from the visible nav on Jay's
      // request — infra (route, page, guide, PersonaLandingPage) is
      // preserved so we can restore in one line when we're ready to
      // push brand-side pitches.
      ...(hideBrandsAndPrograms ? [] : [
        { to: '/for-programs', label: 'For Programs', subtitle: 'Schools, pro orgs, and teams.', icon: GraduationCap, color: '#34d399' },
      ]),
      // iter516k · Removed "Team" from every hamburger menu — pointed
      // to /about which is a static team page not needed in the athlete-
      // first flow. Route + page kept intact for later restore.
      // { to: '/about', label: 'Team', subtitle: 'The people behind Flyt League.', icon: UsersRound, color: '#2dd4bf' },
      // iter816 · Jay: remove "Contact" from the hamburger menu.
      // { to: '/contact', label: 'Contact', subtitle: 'Get in touch with Jay.', icon: Mail, color: '#fb7185' },
    ];
    overlayGroups = [
      {
        title: 'Flyt League',
        items: leagueItems,
      },
      ...(showFlytStudiosGroup ? [{
        title: 'FLYT Studios',
        items: [
          { to: '/flyt', label: 'Studios', subtitle: 'The studio powering flyt league.', icon: Clapperboard, color: '#facc15' },
          { to: '/flyt/process', label: 'Process', subtitle: 'How we build brand + athlete stories.', icon: Workflow, color: '#22d3ee' },
          { to: '/flyt/brands', label: 'Brands', subtitle: "Case studies with the world's boldest brands.", icon: Award, color: '#f472b6' },
          { to: '/flyt/about', label: 'About', subtitle: 'Meet the studio team.', icon: Info, color: '#60a5fa' },
          { to: '/flyt/contact', label: 'Contact', subtitle: 'Start a project with FLYT Studios.', icon: MessageCircle, color: '#fb7185' },
        ],
      }] : []),
    ];
  }

  // ── Persona-specific console groups (iter46) ──────────────────
  // Injected AT THE TOP of the overlay so agents/brands/programs land
  // on their workspace links first. Generic site nav (Flyt League +
  // Studios) still lives below so every user keeps the "back to the
  // network" affordance.
  const buildPersonaGroup = () => {
    if (persona === 'agent') {
      // iter229l · Agents said the menu duplicates the Launchpad on
      // /agent/dashboard AND the drawer links were half-broken. Since
      // the Launchpad handles every console section, we drop the
      // agent-only menu group entirely. Agents still see the generic
      // Flyt League + Help groups below.
      return null;
    }
    if (persona === 'brand') {
      return {
        title: 'Brand Console',
        accent: '#FBBF24',
        items: [
          { to: '/brand/dashboard',    label: 'Overview',       subtitle: 'Your active campaigns at a glance.',  icon: LayoutGrid, color: '#FBBF24' },
          { to: '/athletes',           label: 'Discover Athletes', subtitle: 'Search + shortlist talent.',        icon: Search,     color: '#fbbf24' },
          { to: '/brand/dashboard?view=shortlists', label: 'Shortlists', subtitle: 'Your saved athlete lists.',  icon: Sparkles,   color: '#facc15' },
          { to: '/brand/dashboard?view=campaigns', label: 'Campaigns',   subtitle: 'Active + completed deals.',    icon: Megaphone,  color: '#fb923c' },
          { to: '/roster/messages',    label: 'Messages',       subtitle: 'Chat directly with agents.',          icon: MessageCircle, color: '#e879f9' },
          // iter516j · Flyt Studios entry point hidden while athlete
          // onboarding is the priority focus.
          // { to: '/flyt-studios',       label: 'Pro Assets · Flyt Studios', subtitle: 'Production support for big campaigns.', icon: Briefcase, color: '#F59E0B' },
        ],
      };
    }
    if (persona === 'program') {
      return {
        title: 'Program Console',
        accent: '#A78BFA',
        items: [
          { to: '/program/dashboard', label: 'Overview',     subtitle: "Your department at a glance.",       icon: LayoutGrid,   color: '#A78BFA' },
          { to: '/athletes',          label: 'Discover Talent', subtitle: 'Scout across the network.',         icon: Search,       color: '#c084fc' },
          { to: '/program/dashboard?view=roster',   label: 'Program Roster', subtitle: 'Athletes at your school.',        icon: Users,        color: '#a78bfa' },
          { to: '/program/dashboard?view=recruits', label: 'Recruits',       subtitle: 'Tracked prospects + film.',       icon: TrendingUp,   color: '#60a5fa' },
          { to: '/program/dashboard?view=messages', label: 'Inbox',          subtitle: 'Messages from athletes + agents.', icon: MessageCircle,color: '#e879f9' },
          { to: '/flyt-studios',      label: 'Pro Assets · Flyt Studios', subtitle: 'Production for your program.', icon: Briefcase,  color: '#F59E0B' },
        ],
      };
    }
    if (persona === 'athlete') {
      return {
        title: 'Athlete Console',
        accent: '#60A5FA',
        items: [
          { to: '/profile',                      label: 'My Profile',            subtitle: 'Career score · opportunities.',            icon: Award, color: '#60A5FA' },
          // iter688i · "Career" removed — it deep-linked to the same
          // /profile screen as My Profile (duplicate). Career tab still
          // reachable inside the profile itself.
          // iter516k · Earnings hidden from the hamburger — payouts UI
          // still accessible inside /profile. Restore when brand deals
          // become a primary athlete flow again.
          // { to: '/profile?tab=earnings',         label: 'Earnings',              subtitle: 'Payouts + brand deals.',                   icon: DollarSign, color: '#34d399' },
          { to: '/profile?tab=integrate',        label: 'Build FLYT ID™',         subtitle: 'Your Instagram bio link.',                 icon: LayoutGrid, color: '#c084fc' },
          // iter668j · Messages row removed for first-round launch —
          // the /roster/messages destination dumps athletes into the
          // "Become an agent" empty state (no roster to chat with),
          // which reads like a broken link. Restore once athlete ⇄
          // agent DMs actually ship on the athlete side.
          // { to: '/roster/messages',              label: 'Messages',              subtitle: 'Chat with your agent + brands.',           icon: MessageCircle, color: '#e879f9' },
          // iter748 · Billing & Plan / Athlete Premium moved out of this
          // list entirely — Jay: two separate entries for basically the
          // same thing was confusing (tapping "Athlete Premium" while
          // already subscribed showed a "go premium" pitch instead of
          // their actual plan). Merged into a single "Athlete Premium"
          // entry that lives in the Account section below, next to
          // Manage Subscription (the `persona === 'athlete'` block near
          // accountItems). `/billing` (BillingPage) already renders the
          // right thing either way: the free-plan pitch when not
          // subscribed, the active-plan/cancel controls when they are.
          // iter512 · Apple 1.2 UGC — mandatory user-visible way to
          // see + manage blocked users. Required for App Store review.
          { to: '/me/blocked',                   label: 'Blocked users',         subtitle: 'See and unblock anyone you\u2019ve muted.',    icon: ShieldOff, color: '#f87171' },
          // iter369 · Payouts drawer link removed pre-launch — reinstate
          // when Stripe Connect payouts come back on.
          // iter511 · Settings row removed — the /profile?edit=1 target
          // wasn't wired up so it dead-ended. Re-add once a real
          // settings surface exists.
        ],
      };
    }
    return null;
  };
  const personaGroup = buildPersonaGroup();
  if (personaGroup) {
    overlayGroups = [personaGroup, ...overlayGroups];
  }

  // iter574 · Program-persona menu simplification. Per user brief:
  // program owners get a clutter-free menu — one entry to open their
  // staff dashboard. Everything else (Discover Talent / Program
  // Roster / Recruits / Inbox / Pro Assets, and the whole FLYT League
  // block of Network / Stories / Collections / For …) is removed.
  // Help + Account rows still render below via the shared blocks so
  // Sign Out remains reachable.
  // iter816 · Jay: removed "Contact" from the hamburger menu — the
  // "Flyt League" group existed solely to hold it, so it's gone too.
  if (persona === 'program') {
    overlayGroups = [
      {
        title: 'Program Console',
        accent: '#A78BFA',
        items: [
          {
            to: '/school/dashboard',
            label: 'View Dashboard',
            subtitle: 'Open your program dashboard.',
            icon: LayoutGrid,
            color: '#A78BFA',
          },
        ],
      },
    ];
  }

  // iter229l · "Help" group at the bottom of every menu — one tap to
  // file a support ticket. Deliberately kept as its own group (not a
  // link inside another) so it feels like an escape hatch across
  // Flyt League, FLYT Studios, and all persona consoles.
  overlayGroups = [
    ...overlayGroups,
    {
      title: 'Help',
      items: [
        {
          to: '/support',
          label: 'Get Help',
          subtitle: 'Open a ticket — we usually reply within 24 hours.',
          icon: LifeBuoy,
          color: '#60A5FA',
        },
      ],
    },
  ];
  // iter516k · Account actions (Sign Out + Delete Account) live at the
  // very bottom of the hamburger for every signed-in persona. Removed
  // from the profile-card header because they crowded the primary
  // profile actions. Rendered as buttons (onClick) instead of Links.
  if (user) {
    // iter526 · Agents don't have a persona group (agent group returns
    // null upstream), so they never see the "Billing & Plan" chip that
    // athletes get in their persona group at line 371. Surface it here
    // in the Account section so agents can see + manage their
    // subscription tier (Solo / Team / Enterprise) from the same place
    // they Sign Out or Delete. `/billing` renders BillingPage which
    // routes agents into the Stripe portal for cancel + payment method
    // management.
    const isAgent = ((user?.persona || '') || '').toLowerCase() === 'agent';
    const accountItems = [];
    if (isAgent) {
      accountItems.push({
        to: '/billing',
        label: 'Billing & Plan',
        subtitle: 'Your subscription tier · payment method · cancel anytime.',
        icon: CreditCard,
        color: '#fbbf24',
      });
    }
    // iter549 · Manage Subscription available to EVERY signed-in
    // persona (athlete / agent / brand / program). Opens iOS Settings
    // → Apple ID → Subscriptions directly.
    accountItems.push({
      label: 'Manage Subscription',
      subtitle: 'Change plan or cancel · Apple ID → Subscriptions.',
      icon: ExternalLink,
      color: '#a78bfa',
      onClick: () => {
        const iosUrl = 'itms-apps://apps.apple.com/account/subscriptions';
        const webUrl = 'https://apps.apple.com/account/subscriptions';
        try {
          window.location.href = iosUrl;
          setTimeout(() => { window.open(webUrl, '_blank', 'noopener'); }, 400);
        } catch { window.open(webUrl, '_blank', 'noopener'); }
      },
    });
    // iter748 · One "Athlete Premium" entry, right under Manage
    // Subscription — replaces the old separate "Billing & Plan" (mid-
    // list) + "Athlete Premium" (a hard paywall at /upgrade) pair.
    // `/billing` (BillingPage) is already state-aware: not subscribed →
    // shows the free-plan pitch + "Start free trial"/"Renew" button;
    // subscribed → shows the active plan, days left, price, and Pause/
    // Cancel. So one link does both jobs without the athlete ever
    // landing on a "go premium" screen while already premium.
    if (persona === 'athlete') {
      accountItems.push({
        to: '/billing',
        label: 'Athlete Premium',
        subtitle: 'Your plan · payments · cancel anytime.',
        icon: Sparkle,
        color: '#22d3ee',
      });
    }
    accountItems.push(
      {
        label: 'Sign Out',
        subtitle: `Signed in as ${user.email || 'this device'}.`,
        icon: LogOut,
        color: '#94a3b8',
        onClick: async () => {
          try { if (typeof logout === 'function') await logout(); }
          catch (e) { /* noop — logout will still nuke local state */ }
        },
      },
    );
    // iter862 · Jay: lets an athlete replay the welcome walkthrough any
    // time (including one who's already signed in and never got a fresh
    // signup to trigger it). Navigates home with `?tour=1`, which
    // WelcomeSpotlightTour force-opens on regardless of
    // `has_seen_app_tour`. Athlete-only — the tour's content (Launch
    // Pad, Flyt ID, Media Hub) doesn't apply to other personas.
    if (persona === 'athlete') {
      accountItems.push({
        label: 'Walkthrough',
        subtitle: 'Replay the welcome tour of your Launch Pad, Media Hub, and FLYT ID.',
        icon: Compass,
        color: '#38bdf8',
        onClick: () => { navigate('/?tour=1'); },
      });
    }
    accountItems.push(
      {
        label: 'Delete Account',
        subtitle: 'Permanently remove your Flythouse account.',
        icon: Trash2,
        color: '#f87171',
        destructive: true,
        // Route to /profile with a query flag; ProfilePage listens
        // for `?delete=1` and opens the existing confirm modal.
        onClick: () => { navigate('/profile?delete=1'); },
      },
    );
    overlayGroups = [
      ...overlayGroups,
      {
        title: 'Account',
        items: accountItems,
      },
    ];
  }

  const HamburgerButton = (
    <button
      type="button"
      onClick={() => {
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          try { navigator.vibrate(12); } catch (e) { /* noop */ }
        }
        setMenuOpen(true);
      }}
      aria-label="Open menu"
      aria-expanded={menuOpen}
      className={`w-10 h-10 rounded-full inline-flex items-center justify-center transition active:scale-90 ring-1 ${
        isNetworkProfilePage
          ? 'text-white ring-black/0 hover:brightness-110'
          : isDark
            ? 'text-white ring-white/20 hover:bg-white/10'
            : 'text-black ring-black/10 hover:bg-black/5'
      }`}
      style={isNetworkProfilePage
        ? { backgroundColor: '#000' }
        : isDark ? {
            backgroundColor: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          } : { backgroundColor: 'rgba(0,0,0,0.02)' }}
      data-testid="hamburger-toggle"
    >
      <Menu className="w-5 h-5" />
    </button>
  );

  return (
    <nav
      className={`sticky-nav ${isFlyt ? 'flyt-nav-dark' : ''} ${isDark && !isFlyt ? 'liquid-glass-nav' : ''}`}
      data-testid="navbar"
      style={isDark && !isFlyt ? {
        backgroundColor: 'rgba(20, 20, 22, 0.55)',
        backdropFilter: 'blur(28px) saturate(180%)',
        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        borderBottom: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 18px 50px -22px rgba(0,0,0,0.55)',
      } : undefined}
    >
      {isDark && !isFlyt && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-6 top-0 h-px"
          style={{ background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.32), transparent)' }}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative">
        {/* iter689g · "Update" pill removed — no longer needed. */}
        {/* Unified nav — wordmark LEFT, Pulse pill + hamburger RIGHT.
            Desktop used to spread inline nav links across the bar; we
            collapsed them into the hamburger to match iPad/mobile and
            keep the header uncluttered at every breakpoint. */}
        <div className="flex items-center justify-between h-16 gap-2">
          <div className="min-w-0 flex-1">
            <NavbarWordmark isDark={isDark} />
          </div>
          <div className="flex-shrink-0 flex items-center gap-1.5">
            <PulsePill isDark={isDark} solid={isNetworkProfilePage} />
            {/* iter187 · Profile-avatar-with-dropdown on iPad + desktop
                (>= sm breakpoint). Mobile hides it entirely — the
                hamburger already surfaces every profile shortcut and
                the avatar was crowding the top bar next to Pulse. */}
            {user ? (
              <div className="hidden sm:block">
                <NavbarAuthControl isDark={isDark} />
              </div>
            ) : null}
            {HamburgerButton}
          </div>
        </div>
      </div>

      <HamburgerOverlay open={menuOpen} onClose={() => setMenuOpen(false)} groups={overlayGroups} user={user} onLogin={() => { setMenuOpen(false); if (setLoginOpen) setLoginOpen(true); else navigate('/#login'); }} />
    </nav>
  );
};

export default Navbar;
